import type {
  AuditEvent,
  BehaviorCorrection,
  ConstitutionVersion,
  EvidenceStatement,
  ExternalSummaryRecord,
  PrivateSourceRecord,
  StructuredCEORecord,
} from "../domain/types";
import {
  assertEvidenceStatement,
  assertPrivateSourceRecord,
  materialRevisionRequiresAudit,
  validateConstitutionWrite,
  validateDecision,
  validateExternalSummary,
  validateNewIdea,
  validatePromise,
  validateSingleActiveCorrection,
} from "../rules/invariants";
import { assertNoIssues } from "../rules/errors";
import { createAuditEvent } from "./audit";
import type { CEOOSDatabase } from "./database";

function isEvidenceStatement(record: StructuredCEORecord): record is EvidenceStatement {
  return record.recordType === "evidence_statement";
}

function isBehaviorCorrection(record: StructuredCEORecord): record is BehaviorCorrection {
  return record.recordType === "behavior_correction";
}

function validateStructuredRecord(
  record: StructuredCEORecord,
  context: {
    statementsById: ReadonlyMap<string, EvidenceStatement>;
    existingCorrections: readonly BehaviorCorrection[];
  },
): void {
  if (isEvidenceStatement(record)) {
    assertEvidenceStatement(record, { statementsById: context.statementsById });
  }
  if (record.recordType === "idea_parking") {
    assertNoIssues(validateNewIdea(record));
  }
  if (record.recordType === "promise") {
    assertNoIssues(validatePromise(record));
  }
  if (record.recordType === "decision") {
    assertNoIssues(validateDecision(record));
  }
  if (record.recordType === "behavior_correction") {
    assertNoIssues(validateSingleActiveCorrection(record, context.existingCorrections));
  }
}

export async function savePrivateSource(
  database: CEOOSDatabase,
  record: PrivateSourceRecord,
): Promise<void> {
  assertPrivateSourceRecord(record);
  const audit = createAuditEvent({
    entityType: "PrivateSourceRecord",
    entityId: record.id,
    action: "create",
    actor: "user",
    reason: "用户创建私人原始记录。",
  });
  await database.transaction("rw", database.privateSources, database.auditEvents, async () => {
    await database.privateSources.put(record);
    await database.auditEvents.put(audit);
  });
}

export async function saveStructuredRecord(
  database: CEOOSDatabase,
  record: StructuredCEORecord,
  auditEvent?: AuditEvent,
): Promise<void> {
  const [existing, statements, corrections] = await Promise.all([
    database.structuredRecords.get(record.id),
    database.structuredRecords.where("recordType").equals("evidence_statement").toArray(),
    record.recordType === "behavior_correction"
      ? database.structuredRecords
          .where("[recordType+effectiveDate]")
          .equals(["behavior_correction", record.effectiveDate])
          .toArray()
      : Promise.resolve([]),
  ]);
  const statementRecords = statements.filter(isEvidenceStatement);
  const existingCorrections: BehaviorCorrection[] = corrections
    .filter(isBehaviorCorrection)
    .map((item) => item as BehaviorCorrection);
  validateStructuredRecord(record, {
    statementsById: new Map(statementRecords.map((item) => [item.id, item])),
    existingCorrections,
  });

  if (existing) {
    assertNoIssues(materialRevisionRequiresAudit(existing, record, auditEvent));
  }
  const audit =
    auditEvent ??
    createAuditEvent({
      entityType: record.recordType,
      entityId: record.id,
      action: existing ? "update" : "create",
      actor: "user",
      reason: existing ? "用户更新结构化治理记录。" : "用户创建结构化治理记录。",
    });
  await database.transaction("rw", database.structuredRecords, database.auditEvents, async () => {
    await database.structuredRecords.put(record);
    await database.auditEvents.put(audit);
  });
}

export async function saveExternalSummary(
  database: CEOOSDatabase,
  summary: ExternalSummaryRecord,
): Promise<void> {
  const privateIds = new Set(await database.privateSources.toCollection().primaryKeys());
  assertNoIssues(validateExternalSummary(summary, privateIds));
  const audit = createAuditEvent({
    entityType: "ExternalSummaryRecord",
    entityId: summary.id,
    action: "create",
    actor: "user",
    reason: "用户确认最小化外部摘要。",
  });
  await database.transaction("rw", database.externalSummaries, database.auditEvents, async () => {
    await database.externalSummaries.put(summary);
    await database.auditEvents.put(audit);
  });
}

export async function saveConstitution(
  database: CEOOSDatabase,
  candidate: ConstitutionVersion,
): Promise<void> {
  const previous = candidate.supersedesVersionId
    ? await database.constitutions.get(candidate.supersedesVersionId)
    : undefined;
  assertNoIssues(validateConstitutionWrite(candidate, previous));
  const audit = createAuditEvent({
    entityType: "ConstitutionVersion",
    entityId: candidate.id,
    action: previous ? "supersede" : "create",
    actor: "user",
    reason: candidate.status === "active" ? "用户明确批准宪法版本。" : "创建宪法草案。",
  });
  await database.transaction("rw", database.constitutions, database.auditEvents, async () => {
    await database.constitutions.put(candidate);
    await database.auditEvents.put(audit);
  });
}

export async function hardDeletePrivateSource(
  database: CEOOSDatabase,
  privateSourceId: string,
  now = new Date().toISOString(),
): Promise<void> {
  await database.transaction(
    "rw",
    database.privateSources,
    database.structuredRecords,
    database.auditEvents,
    async () => {
      const existing = await database.privateSources.get(privateSourceId);
      if (!existing) {
        return;
      }
      const linked = await database.structuredRecords
        .where("sourceRecordIds")
        .equals(privateSourceId)
        .toArray();
      for (const record of linked) {
        await database.structuredRecords.put({
          ...record,
          sourceRecordIds: record.sourceRecordIds.filter((id) => id !== privateSourceId),
          source: {
            ...record.source,
            referenceIds: record.source.referenceIds.filter((id) => id !== privateSourceId),
          },
          updatedAt: now,
        });
        await database.auditEvents.put(
          createAuditEvent({
            entityType: record.recordType,
            entityId: record.id,
            action: "detach_reference",
            actor: "system",
            reason: "私人原文永久删除后解除结构化记录引用。",
            changedFields: ["sourceRecordIds", "source.referenceIds"],
            now,
          }),
        );
      }
      await database.privateSources.delete(privateSourceId);
      await database.auditEvents.put(
        createAuditEvent({
          entityType: "PrivateSourceRecord",
          entityId: privateSourceId,
          action: "hard_delete",
          actor: "user",
          reason: "用户永久删除私人原文；审计事件不保留正文。",
          changedFields: ["rawText"],
          now,
        }),
      );
    },
  );
}

export async function softDeleteStructuredRecord(
  database: CEOOSDatabase,
  recordId: string,
  now = new Date().toISOString(),
): Promise<void> {
  await database.transaction("rw", database.structuredRecords, database.auditEvents, async () => {
    const existing = await database.structuredRecords.get(recordId);
    if (!existing) {
      return;
    }
    await database.structuredRecords.put({
      ...existing,
      status: "deleted",
      deletionState: "soft_deleted",
      updatedAt: now,
    });
    await database.auditEvents.put(
      createAuditEvent({
        entityType: existing.recordType,
        entityId: recordId,
        action: "soft_delete",
        actor: "user",
        reason: "用户软删除结构化记录。",
        now,
      }),
    );
  });
}
